import { useState, useCallback, useMemo } from "react";

export interface UseMultiSelectReturn<T extends { id: string }> {
  selectedIds: Set<string>;
  isSelectionMode: boolean;
  isSelected: (id: string) => boolean;
  toggleSelection: (id: string) => void;
  selectAll: (items: T[]) => void;
  clearSelection: () => void;
  toggleSelectionMode: () => void;
  selectedCount: number;
  getSelectedItems: (items: T[]) => T[];
}

export function useMultiSelect<T extends { id: string }>(): UseMultiSelectReturn<T> {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds]
  );

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const selectAll = useCallback((items: T[]) => {
    setSelectedIds(new Set(items.map((item) => item.id)));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setIsSelectionMode(false);
  }, []);

  const toggleSelectionMode = useCallback(() => {
    setIsSelectionMode((prev) => {
      if (prev) {
        setSelectedIds(new Set());
      }
      return !prev;
    });
  }, []);

  const selectedCount = useMemo(() => selectedIds.size, [selectedIds]);

  const getSelectedItems = useCallback(
    (items: T[]) => items.filter((item) => selectedIds.has(item.id)),
    [selectedIds]
  );

  return {
    selectedIds,
    isSelectionMode,
    isSelected,
    toggleSelection,
    selectAll,
    clearSelection,
    toggleSelectionMode,
    selectedCount,
    getSelectedItems,
  };
}
